// WALLET-P1 §6.2: Operator › Billing › Price list — the platform tariff's versions, newest
// first. "New version" and a row's Edit (a new version prefilled from it) open the same modal;
// Remove retires a version not yet in effect, shown disabled with the reason on any other row.
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  FieldError,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PencilIcon, PlusIcon, TagIcon, Trash2Icon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../../api/client";
import { bpText, rateText } from "../../billing/billing-money";
import { DisabledRowAction } from "../../billing/billing-parts";
import { billingKeys, type CatalogTariffRow } from "../../billing/billing-queries";
import { formatDate } from "../../format";
import { problemText } from "../../ops-parts";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { TariffDialog, type TariffRates } from "./tariff-dialog";

export function PriceList({ api }: { api: Api }) {
  const qc = useQueryClient();
  const tariffs = useQuery({
    queryKey: billingKeys.tariffs(),
    queryFn: () => api.client.operator.tariffs.list(),
  });
  const [editing, setEditing] = useState<{ from: TariffRates | null } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: billingKeys.operator });
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="price-list">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Price list</h2>
        <Button size="sm" onClick={() => setEditing({ from: null })}>
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          New version
        </Button>
      </div>
      {tariffs.isPending ? (
        <Loading label="Loading the price list" rows={3} />
      ) : tariffs.isError ? (
        <LoadError what="The price list" error={tariffs.error} />
      ) : tariffs.data.data.length === 0 ? (
        <EmptyState variant="card" icon={TagIcon} title="No price versions yet" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="tariffs">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Version</TableHead>
              <TableHead scope="col">Label</TableHead>
              <TableHead scope="col">Effective from</TableHead>
              <TableHead scope="col" className="text-right">
                Input / 1M
              </TableHead>
              <TableHead scope="col" className="text-right">
                Output / 1M
              </TableHead>
              <TableHead scope="col" className="text-right">
                Image
              </TableHead>
              <TableHead scope="col" className="text-right max-lg:hidden">
                Units
              </TableHead>
              <TableHead scope="col" className="text-right max-md:hidden">
                Markup
              </TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tariffs.data.data.map((t) => (
              <TableRow key={t.id} data-tariff={t.version} className="group/row">
                <TableCell className="tabular-nums">v{t.version}</TableCell>
                <TableHead scope="row" className="font-normal">
                  {t.label}
                </TableHead>
                <TableCell className="tabular-nums">{formatDate(t.effective_from)}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateText(t.input_per_m_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateText(t.output_per_m_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {rateText(t.image_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums max-lg:hidden" data-slot="units">
                  <span
                    title={t.unit_prices
                      .map((u) => `${u.label}: ${rateText(u.price_micros)}/${u.per}`)
                      .join("\n")}
                  >
                    {t.unit_prices.length}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums max-md:hidden">
                  {bpText(t.reported_cost_markup_bp)}
                </TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit v${t.version}`}
                      tip="New version from this one"
                      onClick={() => setEditing({ from: t })}
                    />
                    {t.retirable ? (
                      <RetireTariff api={api} row={t} onDone={refresh} />
                    ) : (
                      <DisabledRowAction
                        icon={Trash2Icon}
                        label={`Remove v${t.version}`}
                        why="In effect: versions in use are kept"
                      />
                    )}
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <TariffDialog
        api={api}
        open={editing !== null}
        onOpenChange={(o) => (o ? null : setEditing(null))}
        title="New price version"
        from={editing?.from ?? null}
        onSaved={refresh}
      />
    </section>
  );
}

function RetireTariff({
  api,
  row,
  onDone,
}: {
  api: Api;
  row: CatalogTariffRow;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const retire = useMutation({
    mutationFn: () => api.client.operator.tariffs.retire({ params: { tariff_id: row.id } }),
    onSuccess: () => {
      setOpen(false);
      onDone();
    },
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <RowAction
        icon={Trash2Icon}
        label={`Remove v${row.version}`}
        tip="Remove"
        onClick={() => setOpen(true)}
      />
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove v{row.version}?</AlertDialogTitle>
          <AlertDialogDescription>
            “{row.label}” will not take effect on {formatDate(row.effective_from)}.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {retire.isError ? (
          <FieldError>{problemText(retire.error, "The version was not removed.")}</FieldError>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={retire.isPending} onClick={() => retire.mutate()}>
            {retire.isPending ? "Removing…" : "Remove"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
